import { TncUpdatePayload } from '../../../application/domain/entities/TncUpdatePayload';
import { TncDetailsRequestDto } from '../dto/req/TncDetailsRequestDto';
import { TncDetailsResponseDto } from '../dto/res/TncDetailsResponseDto';
import { TncCase } from '../../../application/domain/entities/TncCase';
import { EmployeeDecision, TncStep } from '../../../application/domain/enums';

// ─── PUT Request Mapper ───────────────────────────────────────────────────────

/**
 * Maps the domain-level TncUpdatePayload to the API request DTO.
 * Only fields with defined values are included — undefined fields are omitted
 * so the server-side merge is not disrupted.
 */
export function mapTncPayloadToDto(payload: TncUpdatePayload): TncDetailsRequestDto {
  const dto: TncDetailsRequestDto = {};

  if (payload.isEmployeeAvailable !== undefined) {
    dto.isEmployeeAvailable = payload.isEmployeeAvailable;
  }
  if (payload.isPersonallyDiscuss !== undefined) {
    dto.isPersonallyDiscuss = payload.isPersonallyDiscuss;
  }
  if (payload.employeeDecision !== undefined) {
    dto.employeeDecision = payload.employeeDecision;
  }
  if (payload.employerSignature !== undefined) {
    dto.employerSignature = payload.employerSignature;
  }
  if (payload.employerSignatureDate !== undefined) {
    dto.employerSignatureDate = payload.employerSignatureDate;
  }
  // Only send employeeSignature when it has a real value, OR when the employee
  // has explicitly declined to sign (in which case an empty string is intentional).
  if (
    payload.employeeSignature !== undefined &&
    (payload.employeeSignature !== '' || payload.isEmployeeDeclinedToSign === true)
  ) {
    dto.employeeSignature = payload.employeeSignature;
  }
  if (payload.employeeSignatureDate !== undefined) {
    dto.employeeSignatureDate = payload.employeeSignatureDate;
  }
  if (payload.employerRepresentativeName !== undefined) {
    dto.employerRepresentativeName = payload.employerRepresentativeName;
  }
  if (payload.isEmployeeDeclinedToSign !== undefined) {
    dto.isEmployeeDeclinedToSign = payload.isEmployeeDeclinedToSign;
  }

  return dto;
}

// ─── Utility helpers ──────────────────────────────────────────────────────────

/** Maps a UI decision string to the API enum value */
export function mapDecisionToApiValue(uiDecision: string): string {
  switch (uiDecision) {
    case 'contest':
      return 'CONTEST';
    case 'no_contest':
      return 'NOT_CONTEST';
    case 'incorrect':
      return 'INFORMATION_NOT_CORRECT';
    default:
      return uiDecision.toUpperCase();
  }
}

/** Returns today's date in YYYY-MM-DD format as required by the API */
export function getTodayIsoDate(): string {
  return new Date().toISOString().split('T')[0];
}

// ─── GET Response Mapper ──────────────────────────────────────────────────────

/**
 * Derives the resume step from the persisted TNC state.
 *
 * Rules (in priority order):
 *  1. Both signatures present  → DOWNLOAD_RDC (step 8) or CASE_CLOSURE
 *  2. Employee decision present → SIGNATURE (step 7)
 *  3. isPersonallyDiscuss set  → EMPLOYEE_DECISION (step 6)
 *  4. isEmployeeAvailable set  → TNC_INSTRUCTIONS (step 5) or EMAIL_FAN (step 4)
 *  5. Default                  → START_TNC (step 2)
 */
function deriveCurrentStep(dto: TncDetailsResponseDto): TncStep {
  if (dto.employeeSignedAcknowledgment && dto.employerSignedConfirmation) {
    return TncStep.DOWNLOAD_RDC;
  }
  if (dto.employeeDecision !== null) {
    return TncStep.SIGNATURE;
  }
  if (dto.isPersonallyDiscuss !== null) {
    return TncStep.EMPLOYEE_DECISION;
  }
  if (dto.isEmployeeAvailable !== null) {
    return dto.isEmployeeAvailable ? TncStep.TNC_INSTRUCTIONS : TncStep.EMAIL_FAN;
  }
  return TncStep.START_TNC;
}

/**
 * Maps a GET /e-verify/tnc-details/{caseId} response DTO to the TncCase domain entity.
 */
export function mapTncResponseDtoToCase(dto: TncDetailsResponseDto): TncCase {
  return {
    // Identifiers
    everifyCaseTncDetailsId: dto.everifyCaseTncDetailsId,
    caseId: dto.everifyCaseId,

    // TNC type & referral
    tncType: dto.tncType,
    isReferralIssued: dto.isReferralIssued,
    referralDate: dto.referralDate,
    referralDocUrlEnglish: dto.referralDocUrlEnglish,
    referralDocUrlSpanish: dto.referralDocUrlSpanish,
    referralStatus: dto.referralStatus,
    dhsReferralStatus: dto.dhsReferralStatus,
    ssaReferralStatus: dto.ssaReferralStatus,

    // Further Action Notice
    furtherActionNoticeDocUrlEnglish: dto.furtherActionNoticeDocUrlEnglish,
    furtherActionNoticeDocUrlSpanish: dto.furtherActionNoticeDocUrlSpanish,

    // employee details
    employeeName: dto.firstName + ' ' + dto.lastName,
    // Derived step (resume point)
    currentStep: deriveCurrentStep(dto),

    // Step 2 – Employee availability
    employeePresent: dto.isEmployeeAvailable,

    // Step 4 – TNC instructions
    isPersonallyDiscuss: dto.isPersonallyDiscuss,

    // Step 5 – Employee decision
    employeeDecision: dto.employeeDecision as EmployeeDecision | null,

    // Step 7 – Employee signature
    employeeSignedAcknowledgment: dto.employeeSignedAcknowledgment,
    employeeSignature: dto.employeeSignature
      ? { name: dto.employeeSignature, date: dto.employeeSignatureDate ?? '' }
      : null,
    isEmployeeDeclinedToSign: dto.isEmployeeDeclinedToSign,

    // Step 7 – Employer signature
    employerSignedConfirmation: dto.employerSignedConfirmation,
    employerSignature: dto.employerSignature
      ? {
          name: dto.employerSignature,
          date: dto.employerSignatureDate ?? '',
          employerRepresentativeName: dto.employerRepresentativeName ?? undefined,
        }
      : null,
    companyName: dto.companyName,
    employerRepresentativeName: dto.employerRepresentativeName,

    // Timestamps
    actionTimestamp: dto.actionTimestamp,

    caseNumber: dto.caseNumber,
    caseStatus: dto.currentStatus,
    caseStatusDisplay: dto.caseStatusDisplay,
  };
}
